<template>
  <div class="mask" @click.self="closeResult">
    <Fireworks mode="center" :active="showFireworks" />

    <div class="big-win-dialog" role="dialog" aria-modal="true" aria-labelledby="big-win-title">
      <div class="title">
        <h2 id="big-win-title">
          <span>Congratulations on</span>
          <span>Your Big Win!</span>
        </h2>
      </div>

      <div class="winner-panel" style="display: flex;justify-content: center;">
        <div class="winner-list" :class="classNameM">
          <div
              class="winner"
              :class="{'winner--text-only': !prizeImg}"
              v-for="(item, index) in result"
              :key="index"
              :title="item?.winnerName || item"
          >
            <img
                v-if="prizeImg"
                :src="resolveResourceUrl(prizeImg)"
                :alt="prizeName"
                class="winner-prize"
            >
            <span>{{ item?.winnerName || item }}</span>
          </div>
        </div>
      </div>

      <button type="button" class="confirm" aria-label="Confirm" @click.stop="closeResult">
        Confirm
      </button>
    </div>
  </div>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from 'vue'
  import Fireworks from '@/components/Fireworks.vue'
  import {resolveResourceUrl} from '@/utils/transform'

  const prop = defineProps({
    result: {
      type: Array,
      default: () => []
    },
    prizeName: {
      type: String,
      default: ''
    },
    prizeImg: {
      type: String,
      default: ''
    }
  })

  const className = computed(() => {
    if(prop.result.length <= 4) {
      return 'grid-cols-'+prop.result.length
    }else {
      return 'grid-cols-4'
    }
  })
  const classNameM = computed(() => {
    if(prop.result.length <= 3) {
      return 'grid-cols-m-'+prop.result.length
    }else {
      return 'grid-cols-m-3'
    }
  })

  const emit = defineEmits(['closeResult'])
  const remainingSeconds = ref(30)
  const showFireworks = ref(false)
  let countdownTimer = null
  let fireworksTimer = null
  let isClosed = false
  let previousBodyOverflow = ''

  const stopCountdown = () => {
    if(countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }

  const stopFireworks = () => {
    showFireworks.value = false
    if(fireworksTimer) {
      clearTimeout(fireworksTimer)
      fireworksTimer = null
    }
  }

  const closeResult = () => {
    if(isClosed) {
      return
    }

    isClosed = true
    stopCountdown()
    emit('closeResult')
  }

  onMounted(() => {
    previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    showFireworks.value = true
    fireworksTimer = window.setTimeout(stopFireworks, 3200)

    countdownTimer = setInterval(() => {
      if(remainingSeconds.value <= 1) {
        remainingSeconds.value = 0
        closeResult()
        return
      }

      remainingSeconds.value -= 1
    }, 1000)
  })

  onBeforeUnmount(() => {
    stopCountdown()
    stopFireworks()
    document.body.style.overflow = previousBodyOverflow
  })
</script>

<style scoped lang="scss">
$panel: #fff6e8;
$border: #ffc9c5;
$card: #ffdda2;
$red: #d52212;

.mask {
  position: fixed;
  inset: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  overflow: hidden;
  overscroll-behavior: none;
  background: rgba(20, 9, 3, 0.76);
  backdrop-filter: blur(9px);
  -webkit-backdrop-filter: blur(9px);
}

:deep(.fireworks-container) {
  z-index: 1001;
}

.big-win-dialog {
  width: min(680px, calc(100vw - 56px));
  max-height: calc(100vh - 56px);
  position: relative;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  padding: 64px 26px 24px;
  overflow: visible;
  border: 7px solid $border;
  border-radius: 42px;
  background: $panel;
  box-shadow: 0 24px 70px rgba(89, 29, 5, 0.38);
  color: $red;
  font-family: Arial, Helvetica, sans-serif;
  animation: big-win-enter 260ms ease-out both;
}

.title {
  width: min(343px, 58%);
  aspect-ratio: 343 / 84;
  position: absolute;
  top: -31px;
  left: 50%;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 34px;
  background: url("@/assets/frame/btn-bgk.png") center / 100% 100% no-repeat;
  transform: translateX(-50%);

  h2 {
    width: 100%;
    margin: 0;
    overflow: hidden;
    color: #fff;
    font-size: clamp(20px, 1.4583vw, 28px);
    line-height: 1.08;
    font-weight: 800;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;

    span {
      display: inline;

      & + span {
        margin-inline-start: 0.25em;
      }
    }
  }
}

.winner-panel {
  min-height: 178px;
  max-height: min(52vh, 630px);
  overflow: hidden;
  border-radius: 22px;
  background: $card;
}

.winner-list {
  width: 100%;
  max-height: min(52vh, 430px);
  display: grid;
  //grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 178px;
  align-content: start;
  gap: 14px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 18px;
  scrollbar-width: thin;
  scrollbar-color: #ff8a1e transparent;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: #ff8a1e;
  }
}

.winner {
  min-width: 0;
  height: 178px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 12px 10px;
  overflow: hidden;
  border: 2px solid rgba(255, 150, 36, 0.24);
  border-radius: 16px;
  background: rgba(255, 246, 231, 0.88);
  color: #a82b18;
  font-size: 35px;
  line-height: 1.2;
  font-weight: 700;
  text-align: center;
  direction: ltr;

  span {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.winner--text-only {
    min-height: 100px;
  }
}

.winner-prize {
  width: 100%;
  height: 104px;
  object-fit: contain;
}

.confirm {
  width: 240px;
  aspect-ratio: 343 / 84;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px auto 0;
  padding: 0 26px;
  border: 0;
  background: url("@/assets/frame/btn-bgk.png") center / 100% 100% no-repeat;
  color: #fff;
  font-family: inherit;
  font-size: 22px;
  line-height: 1;
  font-weight: 800;
  text-align: center;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid #ffb24a;
    outline-offset: 2px;
  }
}

@keyframes big-win-enter {
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.97);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media screen and (max-width: 768px) {
  .mask {
    padding: 12px;
  }

  .big-win-dialog {
    width: min(343px, calc(100vw - 24px));
    max-height: calc(100vh - 24px);
    padding: 52px 12px 16px;
    border-width: 5px;
    border-radius: 30px;
  }

  .title {
    width: 280px;
    top: -22px;
    padding: 0 26px;

    h2 {
      font-size: 18px;
    }
  }

  .winner-panel {
    min-height: 134px;
    max-height: min(58vh, 390px);
    border-radius: 18px;
  }

  .winner-list {
    max-height: min(58vh, 390px);
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-auto-rows: 142px;
    gap: 8px;
    padding: 10px;
  }

  .winner {
    height: 140px;
    gap: 5px;
    padding: 7px 5px;
    border-width: 1px;
    border-radius: 12px;
    font-size: 14px;
  }

  .winner-prize {
    height: 74px;
  }

  .confirm {
    width: 220px;
    margin-top: 14px;
    font-size: 18px;
  }
}

@media screen and (max-height: 560px) and (orientation: landscape) {
  .big-win-dialog {
    width: min(620px, calc(100vw - 24px));
    max-height: calc(100vh - 20px);
    padding-top: 42px;
  }

  .title {
    width: 240px;
    top: -17px;

    h2 {
      font-size: 17px;
    }
  }

  .winner-panel,
  .winner-list {
    max-height: 48vh;
  }

  .winner-list {
    grid-auto-rows: 100px;
  }

  .winner {
    height: 120px;
  }

  .winner-prize {
    height: 64px;
  }

  .confirm {
    width: 190px;
    margin-top: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .big-win-dialog {
    animation: none;
  }
}
.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}
.grid-cols-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}
.grid-cols-4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
}
.grid-cols-5 {
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 20px;
}
.grid-cols-6 {
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 20px;
}
.grid-cols-m-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 15px;
}
.grid-cols-m-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 15px;
}
.grid-cols-m-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 15px;
}
</style>
